<template>
    <div
        class="overflow-y-auto flex-1 hide_scroll"
        :class="isLoading ? 'opacity-70 pointer-events-none select-none' : ''"
    >
        <div v-if="loadingStates.searching" class="flex mt-2 justify-center items-center gap-2">
            <div class="w-6 h-6 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin"></div>

            <span class="font-semibold">Loading ...</span>
        </div>

        <template v-else>
            <template v-if="alphabeticContacts.length">
                <div v-for="(contactGroup, index) in alphabeticContacts" :key="index" class="mb-4">
                    <p v-if="!searchInput" class="border-b border-gray-150 text-lg pb-2 sticky top-0 bg-white z-[5]">
                        {{ contactGroup.key }}
                    </p>
                    <div class="mt-4">
                        <div
                            v-for="(contact, indexc) in contactGroup.contacts"
                            :key="indexc"
                            class="mb-3 hover:bg-gray-100 p-2 rounded-lg cursor-pointer"
                            :class="
                                disabledContacts.some((mem) => mem.id === contact.id)
                                    ? 'pointer-events-none opacity-70 bg-gray-100'
                                    : ''
                            "
                            @click="() => emits('handleContactClick', contact)"
                        >
                            <ContactNameIcon
                                :nameLetter="contactGroup.key"
                                :nameText="`${contact?.first_name} ${contact?.last_name || ''}`"
                                :lastNameLetter="contact?.last_name?.charAt(0)"
                                :badgeClass="contact.badge"
                                nameTextClass="font-bold"
                                detailClass="text-gray-600"
                                :isSmall="false"
                                :notMb="true"
                                :isFull="true"
                                :email="contact.email"
                                :phone="contact.phone"
                            />
                        </div>
                    </div>
                </div>
            </template>

            <template v-else>
                <div class="flex items-center gap-2 justify-center text-gray-600">
                    <fa :icon="['fal', 'info-circle']"></fa>
                    <p class="text-center text-sm font-semibold my-2">No contact available</p>
                </div>
            </template>
        </template>
    </div>
</template>

<script setup>
import ContactNameIcon from "@/components/contacts/ContactNameIcon.vue";
import { getAutoCompleteResults } from "@/api/AutoComplete";
import { useDebounceFn } from "@vueuse/core";
import { onMounted, ref, watch } from "vue";

// [info]: emits
const emits = defineEmits(["handleContactClick"]);

// [info]: props
const props = defineProps({
    disabledContacts: {
        type: Array,
        default: [],
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    searchQuery: {
        type: String,
        default: "",
    },
});

// [info]: constants
let page = 0;

// [info]: states
const alphabeticContacts = ref([]);
const loadingStates = ref({
    searching: false,
});
const searchInput = ref(""); // [info]: gotta make another state so the keys (A,B,C) wont just visible instantly if the input from props is null

// [info]: methods
const getUsers = async (page, query = "") => {
    searchInput.value = query;
    // [info]: this page is for pagination
    try {
        loadingStates.value.searching = true;
        const response = await getAutoCompleteResults(query, "contact", "");

        if (response.status === "Success") {
            const contactGroups = response.data.contact_groups;
            const alphaContactsMap = new Map();
            contactGroups.forEach((item) => {
                item.contacts.forEach((contact) => {
                    if (!contact.first_name) return;
                    const firstLetter = contact.first_name.charAt(0).toUpperCase();
                    if (alphaContactsMap.has(firstLetter)) {
                        alphaContactsMap.get(firstLetter).push(contact);
                    } else {
                        alphaContactsMap.set(firstLetter, [contact]);
                    }
                });
            });

            alphabeticContacts.value = Array.from(alphaContactsMap.entries())
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, contacts]) => ({ key, contacts }));
        }
    } catch (e) {
        console.error("[ERR]: at getUsers", e);
    } finally {
        loadingStates.value.searching = false;
    }
};

const debounceSearch = useDebounceFn((searchQuery) => getUsers(page, searchQuery), 300);

// [info]: lifecycle
watch(
    () => props.searchQuery,
    () => {
        debounceSearch(props.searchQuery);
    },
);

onMounted(() => {
    getUsers(page);
});
</script>
